import { Component, Input, OnInit } from '@angular/core';
import { Block, BlockAbstractComponent } from '@gallaghercp/core-frontend';
import { BlockTimelineChartComponent } from '@core-app/blocks/block-leave-of-absence-tool/results/timeline-chart/block-timeline-chart.component';
import { TranslatePipe } from '@gallaghercp/core-frontend-contracts';

@Component({
    selector: 'core-sick-or-injured-results',
    templateUrl: './block-sick-or-injured-results.component.html',
    styleUrls: [
        '../../scss/block-leave-of-absence-tool-charts.scss',
        '../../scss/block-leave-of-absence-tool-questions.scss',
        '../../scss/block-leave-of-absence-tool-timeline.scss',
    ],
    imports: [BlockTimelineChartComponent, TranslatePipe],
})
export class BlockSickOrInjuredResultsComponent extends BlockAbstractComponent implements OnInit {
    @Input() selectedAnswers: Record<string, any> = {};
    @Input() sicknessOrInjuryTimelineChartKey: string = '';
    @Input() parentBlock: Block | undefined;

    // Section text strings
    introParagraphText!: string;
    lengthOfLeaveText!: string;
    payText!: string;
    processChecklist!: string;
    thingsToConsider!: string;

    override ngOnInit() {
        // Create a mapping of conditions to their corresponding intro paragraph text
        const introParagraphMapping = [
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyShort,
                text: this.parentBlock?.data.sickOrInjuredIntroParagraphLeaveQualified,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyLong,
                text: this.parentBlock?.data.sickOrInjuredIntroParagraphLeaveQualified,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyRecurring,
                text: this.parentBlock?.data.sickOrInjuredIntroParagraphLeaveQualified,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreSalariedShort,
                text: this.parentBlock?.data.sickOrInjuredIntroParagraphLeaveThreeCalendarDaysStdAtOneHundredPercent,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreSalariedLong,
                text: this.parentBlock?.data.sickOrInjuredIntroParagraphLeaveQualified,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreSalariedRecurring,
                text: this.parentBlock?.data.sickOrInjuredIntroParagraphLeaveQualified,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyShort,
                text: this.parentBlock?.data.sickOrInjuredIntroParagraphLeaveNotQualifiedStdAtSixtyPercent,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong,
                text: this.parentBlock?.data.sickOrInjuredIntroParagraphLeaveQualified,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyRecurring,
                text: this.parentBlock?.data.sickOrInjuredIntroParagraphLeaveNotQualifiedUsePaidTime,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedShort,
                text: this.parentBlock?.data.sickOrInjuredIntroParagraphLeaveNotQualifiedStdAtOneHundredPercent,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong,
                text: this.parentBlock?.data.sickOrInjuredIntroParagraphLeaveQualified,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedRecurring,
                text: this.parentBlock?.data.sickOrInjuredIntroParagraphLeaveNotQualifiedUsePaidTime,
            },
            {
                condition: this.sicknessOrInjuryNinetyDaysOrLess,
                text: this.parentBlock?.data.sickOrInjuredIntroParagraphLeaveNotQualifiedContactHr,
            },
        ];

        // Create a mapping of conditions to their corresponding length of leave text
        const lengthOfLeaveMapping = [
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyShort,
                text: this.parentBlock?.data.sickOrInjuredLengthOfLeaveMoreThanThreeCalendarDaysNotQualified,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyLong,
                text: this.parentBlock?.data.sickOrInjuredLengthOfLeaveUpToTwelveMonths,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyRecurring,
                text: this.parentBlock?.data.sickOrInjuredLengthOfLeaveUpToTwelveWeeks,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreSalariedLong,
                text: this.parentBlock?.data.sickOrInjuredLengthOfLeaveUpToTwelveMonths,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreSalariedRecurring,
                text: this.parentBlock?.data.sickOrInjuredLengthOfLeaveUpToTwelveWeeks,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong,
                text: this.parentBlock?.data.sickOrInjuredLengthOfLeaveUpToSixMonths,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong,
                text: this.parentBlock?.data.sickOrInjuredLengthOfLeaveUpToSixMonths,
            },
        ];

        // Create a mapping of conditions to their corresponding pay text
        const payMapping = [
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyShort,
                text: this.parentBlock?.data.sickOrInjuredPaySevenDaysWaitingPeriodStdAtSixtyPercent,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyLong,
                text: this.parentBlock?.data
                    .sickOrInjuredPaySevenDaysWaitingPeriodStdAtSixtyPercentOverTwentyWeeksLtdAtFiftyPercent,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyRecurring,
                text: this.parentBlock?.data.sickOrInjuredPayUnpaidUnlessUsingPaidTimeOff,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreSalariedLong,
                text: this.parentBlock?.data.sickOrInjuredPayStdAtOneHundredPercentOverTwentyWeeksLtdAtFiftyPercent,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreSalariedRecurring,
                text: this.parentBlock?.data.sickOrInjuredPayUnpaidUnlessUsingPaidTimeOff,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong,
                text: this.parentBlock?.data.sickOrInjuredPaySevenDaysWaitingPeriodStdAtSixtyPercentOnEighthDay,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong,
                text: this.parentBlock?.data.sickOrInjuredPayStdAtOneHundredPercent,
            },
        ];

        // Create a mapping of conditions to their corresponding process list text
        const processListMapping = [
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyShort,
                text: this.parentBlock?.data.sickOrInjuredProcessListOne,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyLong,
                text: this.parentBlock?.data.sickOrInjuredProcessListTwo,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyRecurring,
                text: this.parentBlock?.data.sickOrInjuredProcessListThree,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreSalariedLong,
                text: this.parentBlock?.data.sickOrInjuredProcessListFour,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreSalariedRecurring,
                text: this.parentBlock?.data.sickOrInjuredProcessListThree,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong,
                text: this.parentBlock?.data.sickOrInjuredProcessListTwo,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong,
                text: this.parentBlock?.data.sickOrInjuredProcessListTwo,
            },
        ];

        // Create a mapping of conditions to their corresponding things to consider text
        const thingsToConsiderMapping = [
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyShort,
                text: this.parentBlock?.data.sickOrInjuredThingsToConsiderOne,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyLong,
                text: this.parentBlock?.data.sickOrInjuredThingsToConsiderOne,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreHourlyRecurring,
                text: this.parentBlock?.data.sickOrInjuredThingsToConsiderOne,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreSalariedLong,
                text: this.parentBlock?.data.sickOrInjuredThingsToConsiderOne,
            },
            {
                condition: this.sicknessOrInjuryTwelveMonthsOrMoreSalariedRecurring,
                text: this.parentBlock?.data.sickOrInjuredThingsToConsiderOne,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong,
                text: this.parentBlock?.data.sickOrInjuredThingsToConsiderOne,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyRecurring,
                text: this.parentBlock?.data.sickOrInjuredThingsToConsiderTwo,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong,
                text: this.parentBlock?.data.sickOrInjuredThingsToConsiderOne,
            },
            {
                condition: this.sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedRecurring,
                text: this.parentBlock?.data.sickOrInjuredThingsToConsiderTwo,
            },
            {
                condition: this.sicknessOrInjuryNinetyDaysOrLess,
                text: this.parentBlock?.data.sickOrInjuredThingsToConsiderTwo,
            },
        ];

        // Find the first matching condition and set the intro paragraph text
        const introParagraphMatch = introParagraphMapping.find(item => item.condition);
        if (introParagraphMatch) {
            this.introParagraphText = introParagraphMatch.text;
        }

        // Find the first matching condition and set the length of leave text
        const lengthOfLeaveMatch = lengthOfLeaveMapping.find(item => item.condition);
        if (lengthOfLeaveMatch) {
            this.lengthOfLeaveText = lengthOfLeaveMatch.text;
        }

        // Find the first matching condition and set the pay text
        const payMatch = payMapping.find(item => item.condition);
        if (payMatch) {
            this.payText = payMatch.text;
        }

        // Find the first matching condition and set the process list text
        const processListMatch = processListMapping.find(item => item.condition);
        if (processListMatch) {
            this.processChecklist = processListMatch.text;
        }

        // Find the first matching condition and set the things to consider text
        const thingsToConsiderMatch = thingsToConsiderMapping.find(item => item.condition);
        if (thingsToConsiderMatch) {
            this.thingsToConsider = thingsToConsiderMatch.text;
        }
        super.ngOnInit();
    }

    get sicknessOrInjuryTwelveMonthsOrMoreHourlyShort(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['timeAwayFromWork'] == 'short'
        );
    }

    get sicknessOrInjuryTwelveMonthsOrMoreHourlyLong(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['timeAwayFromWork'] == 'long'
        );
    }

    get sicknessOrInjuryTwelveMonthsOrMoreHourlyRecurring(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['timeAwayFromWork'] == 'recurring'
        );
    }

    get sicknessOrInjuryTwelveMonthsOrMoreSalariedShort(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['timeAwayFromWork'] == 'short'
        );
    }

    get sicknessOrInjuryTwelveMonthsOrMoreSalariedLong(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['timeAwayFromWork'] == 'long'
        );
    }

    get sicknessOrInjuryTwelveMonthsOrMoreSalariedRecurring(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['timeAwayFromWork'] == 'recurring'
        );
    }

    get sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyShort(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['timeAwayFromWork'] == 'short'
        );
    }

    get sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['timeAwayFromWork'] == 'long'
        );
    }

    get sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyRecurring(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['timeAwayFromWork'] == 'recurring'
        );
    }

    get sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedShort(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['timeAwayFromWork'] == 'short'
        );
    }

    get sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['timeAwayFromWork'] == 'long'
        );
    }

    get sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedRecurring(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['timeAwayFromWork'] == 'recurring'
        );
    }

    get sicknessOrInjuryNinetyDaysOrLess(): boolean {
        return this.selectedAnswers['employedDuration'] == 'ninetyDaysOrLess';
    }
}
